import React, { useState, useContext, useEffect } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  InputAdornment,
  IconButton
} from '@mui/material'
import { useForm, SubmitHandler } from 'react-hook-form'
import { SnackbarContext } from 'contexts/SnackbarContext'
import {
  requestPasswordVerificationCode,
  verifyAndUpdatePassword
} from 'api/accounts'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
  passwordMaxLength,
  passwordPattern,
  passwordValidationMessage
} from 'utils/passwordValidation'
import { getApiErrorMessage } from 'utils'

interface FormData {
  email: string
  code: string
  newPassword: string
  confirmNewPassword: string
}

const ResetPasswordPage = () => {
  const { showMessage } = useContext(SnackbarContext)
  const [step, setStep] = useState(1)
  const [isResendDisabled, setIsResendDisabled] = useState(false)
  const [timer, setTimer] = useState(0)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { errors }
  } = useForm<FormData>()

  useEffect(() => {
    let timerId: NodeJS.Timeout
    if (isResendDisabled && timer > 0) {
      timerId = setTimeout(() => {
        setTimer(prev => prev - 1)
      }, 1000)
    } else if (isResendDisabled && timer === 0) {
      setIsResendDisabled(false)
    }
    return () => clearTimeout(timerId)
  }, [isResendDisabled, timer])

  const onEmailSubmit: SubmitHandler<Pick<FormData, 'email'>> = async (
    data: Pick<FormData, 'email'>
  ) => {
    const { email } = data
    if (!email) {
      showMessage('Please enter your email address.')
      return
    }
    try {
      const success = await requestPasswordVerificationCode(email)
      if (success) {
        showMessage('Verification code sent to your email.')
        setStep(2)
        setIsResendDisabled(true)
        setTimer(30)
      } else {
        showMessage('Error sending verification code. Please try again.')
      }
    } catch (error) {
      showMessage(
        getApiErrorMessage(
          error,
          'Error sending verification code. Please try again.'
        )
      )
    }
  }

  const onResetPasswordSubmit: SubmitHandler<FormData> = async data => {
    const { email, code, newPassword } = data
    try {
      const success = await verifyAndUpdatePassword(code, newPassword, email)
      if (success) {
        showMessage('Password reset successfully.')
        setStep(1)
        reset()
      } else {
        showMessage(
          'Password reset failed. Please check your code and try again.'
        )
      }
    } catch (error) {
      showMessage(
        getApiErrorMessage(
          error,
          'Password reset failed. Please check your code and try again.'
        )
      )
    }
  }

  const handleResendCode = async () => {
    const email = getValues('email')
    if (!email) {
      showMessage('Please enter your email address.')
      return
    }
    if (isResendDisabled) return

    try {
      const success = await requestPasswordVerificationCode(email)
      if (success) {
        showMessage('Verification code resent to your email.')
        setIsResendDisabled(true)
        setTimer(30)
      } else {
        showMessage('Error resending verification code. Please try again.')
      }
    } catch (error) {
      showMessage(
        getApiErrorMessage(
          error,
          'Error resending verification code. Please try again.'
        )
      )
    }
  }

  return (
    <Box
      sx={{
        margin: 'auto',
        mt: '100px',
        padding: '20px',
        width: '100%',
        maxWidth: 500,
        borderRadius: '12px',
        boxShadow: '0px 8px 20px #d9d9d9',
        overflow: 'hidden'
      }}
    >
      <Typography variant='h3' gutterBottom>
        Reset Password
      </Typography>

      {step === 1 && (
        <form onSubmit={handleSubmit(onEmailSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label='Email Address'
                variant='outlined'
                fullWidth
                {...register('email', { required: 'Email is required.' })}
                error={!!errors.email}
                helperText={errors.email ? errors.email.message : ''}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                variant='contained'
                color='primary'
                type='submit'
                fullWidth
              >
                Next
              </Button>
            </Grid>
          </Grid>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleSubmit(onResetPasswordSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label='Verification Code'
                variant='outlined'
                fullWidth
                {...register('code', {
                  required: 'Verification code is required.'
                })}
                error={!!errors.code}
                helperText={errors.code ? errors.code.message : ''}
                InputProps={{
                  endAdornment: (
                    <Button
                      onClick={handleResendCode}
                      disabled={isResendDisabled}
                    >
                      {isResendDisabled
                        ? `Resend Code (${timer})`
                        : 'Resend Code'}
                    </Button>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='New Password'
                variant='outlined'
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                {...register('newPassword', {
                  required: 'New password is required.',
                  pattern: {
                    value: passwordPattern,
                    message: passwordValidationMessage
                  }
                })}
                error={!!errors.newPassword}
                helperText={
                  errors.newPassword ? errors.newPassword.message : ''
                }
                inputProps={{ maxLength: passwordMaxLength }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={() => setShowNewPassword(prev => !prev)}
                        edge='end'
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Confirm New Password'
                variant='outlined'
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                {...register('confirmNewPassword', {
                  required: 'Please confirm your new password.',
                  validate: value =>
                    value === getValues('newPassword') ||
                    'Passwords do not match.'
                })}
                error={!!errors.confirmNewPassword}
                helperText={
                  errors.confirmNewPassword
                    ? errors.confirmNewPassword.message
                    : ''
                }
                inputProps={{ maxLength: passwordMaxLength }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={() => setShowConfirmPassword(prev => !prev)}
                        edge='end'
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
          <Box display='flex' justifyContent='space-between' sx={{ mt: 2 }}>
            <Button
              variant='outlined'
              color='secondary'
              onClick={() => setStep(1)}
            >
              Back
            </Button>
            <Button variant='contained' color='primary' type='submit'>
              Reset Password
            </Button>
          </Box>
        </form>
      )}
    </Box>
  )
}

export default ResetPasswordPage
